import React, { memo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, Markdown, useTheme } from '@Sage/hoi-poi-ui';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotBarFeedback');

const CopilotBarFeedback = ({ feedback, onDismiss }) => {
    const classes = useStyles();
    const theme = useTheme();
    const autoHideTimerRef = useRef(null);

    const { autoHide = false, dismissible = false, message } = feedback || {};

    useEffect(() => {
        if (autoHide && autoHide > 0) {
            autoHideTimerRef.current = setTimeout(() => {
                onDismiss?.();
            }, autoHide);
        }

        return () => {
            if (autoHideTimerRef.current) {
                clearTimeout(autoHideTimerRef.current);
                autoHideTimerRef.current = null;
            }
        };
    }, [autoHide, message, onDismiss]);

    const handleDismiss = useCallback(() => {
        if (autoHideTimerRef.current) {
            clearTimeout(autoHideTimerRef.current);
        }
        onDismiss?.();
    }, [onDismiss]);

    if (!message) return null;

    return (
        <div className={classNames(classes.root, { [classes.dismissible]: dismissible })}>
            <Markdown className={classes.message} content={message} />
            {dismissible && (
                <div className={classes.dismissButton} onClick={handleDismiss}>
                    <Icon color={theme.colors.primary.black} name="close" />
                </div>
            )}
        </div>
    );
};

CopilotBarFeedback.propTypes = {
    feedback: PropTypes.shape({
        autoHide: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
        dismissible: PropTypes.bool,
        message: PropTypes.string,
    }),
    onDismiss: PropTypes.func,
};

const MemoizedCopilotBarFeedback = memo(CopilotBarFeedback);
MemoizedCopilotBarFeedback.displayName = 'CopilotBarFeedback';

export default MemoizedCopilotBarFeedback;
